// Selfie liveness check: the client must blink before the photo can be taken,
// so a printed photo or a picture on another screen cannot be used instead.
//
// Uses MediaPipe's face landmarker in the browser. The library and model are
// loaded from their CDNs the first time the camera is started, so they cost
// nothing for users who never open the camera.
//
// A blink is read from the eyeBlinkLeft/eyeBlinkRight blendshape scores. Each
// person's resting level differs, so the baseline is the lowest reading over
// the last REST_WINDOW_MS; the eyes count as closing when the score rises well
// above it and as a blink once it drops back.
//
// The face tracker fits a whole face even when part of it is covered, and a
// hand over the eyes makes the blink readings unreliable. MediaPipe's hand
// landmarker runs beside it: while a hand overlaps the face, nothing counts.

const FACE_LIBRARY = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1';
const FACE_MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const HAND_MODEL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

export const BLINKS_REQUIRED = 2;

// The face must fill at least this share of the frame width.
const MINIMUM_FACE_WIDTH = 0.18;
// Faces narrower than this are ignored, so the photo on an ID card the client
// is holding up does not count as a second person.
const IGNORED_FACE_WIDTH = 0.1;

const BLINK_RISE = 0.25;
const BLINK_FLOOR = 0.35;
const REST_WINDOW_MS = 1500;
const REST_MINIMUM_READINGS = 5;

// A hand counts as over the face when this share of the face's box is covered
// by the hand's box, or a fingertip/knuckle lies inside the face's box.
const HAND_OVERLAP_SHARE = 0.04;

let landmarkerPromise = null;
let handLandmarkerPromise = null;

export function loadLandmarker() {
  if (landmarkerPromise) return landmarkerPromise;
  landmarkerPromise = import(/* webpackIgnore: true */ `${FACE_LIBRARY}/vision_bundle.mjs`)
    .then((vision) =>
      vision.FilesetResolver.forVisionTasks(`${FACE_LIBRARY}/wasm`).then((fileset) =>
        vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: FACE_MODEL, delegate: 'CPU' },
          runningMode: 'VIDEO',
          numFaces: 3,
          outputFaceBlendshapes: true,
        })
      )
    )
    .catch((error) => {
      // Let the next attempt try again rather than caching the failure.
      landmarkerPromise = null;
      throw error;
    });
  return landmarkerPromise;
}

export function loadHandLandmarker() {
  if (handLandmarkerPromise) return handLandmarkerPromise;
  handLandmarkerPromise = import(/* webpackIgnore: true */ `${FACE_LIBRARY}/vision_bundle.mjs`)
    .then((vision) =>
      vision.FilesetResolver.forVisionTasks(`${FACE_LIBRARY}/wasm`).then((fileset) =>
        vision.HandLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: HAND_MODEL, delegate: 'CPU' },
          runningMode: 'VIDEO',
          numHands: 2,
        })
      )
    )
    .catch((error) => {
      handLandmarkerPromise = null;
      throw error;
    });
  return handLandmarkerPromise;
}

function box(points) {
  let minX = 1; let maxX = 0; let minY = 1; let maxY = 0;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, maxX, minY, maxY };
}

// Whether any detected hand covers part of the face.
function handOverFace(faceLandmarks, hands) {
  if (!hands || !hands.length) return false;
  const face = box(faceLandmarks);
  const faceArea = Math.max(1e-6, (face.maxX - face.minX) * (face.maxY - face.minY));
  return hands.some((hand) => {
    if (hand.some((p) => p.x >= face.minX && p.x <= face.maxX && p.y >= face.minY && p.y <= face.maxY)) return true;
    const h = box(hand);
    const w = Math.max(0, Math.min(face.maxX, h.maxX) - Math.max(face.minX, h.minX));
    const t = Math.max(0, Math.min(face.maxY, h.maxY) - Math.max(face.minY, h.minY));
    return (w * t) / faceArea >= HAND_OVERLAP_SHARE;
  });
}

function faceWidth(landmarks) {
  let min = 1;
  let max = 0;
  for (const point of landmarks) {
    if (point.x < min) min = point.x;
    if (point.x > max) max = point.x;
  }
  return max - min;
}

function blinkScore(categories) {
  let left = 0;
  let right = 0;
  for (const c of categories || []) {
    if (c.categoryName === 'eyeBlinkLeft') left = c.score;
    else if (c.categoryName === 'eyeBlinkRight') right = c.score;
  }
  return (left + right) / 2;
}

// Returns { problem, eyes }: a message when the framing is not acceptable,
// otherwise the eye-closed score of the one person in the picture.
// hands: the hand landmarker's landmarks for the same frame, when it runs.
export function readFrame(result, hands = null) {
  const faces = ((result && result.faceLandmarks) || [])
    .map((landmarks, i) => ({ width: faceWidth(landmarks), index: i }))
    .filter((face) => face.width >= IGNORED_FACE_WIDTH);

  if (faces.length === 0) {
    return { problem: 'No face can be seen. Look straight at the camera, in good light.' };
  }
  if (faces.length > 1) {
    return { problem: 'Only you may be in the photo. Make sure nobody else is in the picture.' };
  }
  if (faces[0].width < MINIMUM_FACE_WIDTH) {
    return { problem: 'Come a little closer to the camera.' };
  }
  if (handOverFace(result.faceLandmarks[faces[0].index], hands)) {
    return { problem: 'Keep your hands away from your face. Your whole face must be visible.' };
  }
  const blendshapes = result.faceBlendshapes && result.faceBlendshapes[faces[0].index];
  return { problem: null, eyes: blinkScore(blendshapes && blendshapes.categories) };
}

export function createBlinkCounter() {
  return { blinks: 0, closing: false, readings: [] };
}

export function resetBlinkCounter(counter) {
  counter.blinks = 0;
  counter.closing = false;
  counter.readings = [];
}

// Feeds one reading into the counter and returns the number of blinks so far.
export function countBlink(counter, eyes, now) {
  const readings = counter.readings;
  while (readings.length && now - readings[0].at > REST_WINDOW_MS) readings.shift();
  if (readings.length >= REST_MINIMUM_READINGS) {
    let rest = 1;
    for (const r of readings) if (r.eyes < rest) rest = r.eyes;
    if (!counter.closing && eyes >= Math.max(rest + BLINK_RISE, BLINK_FLOOR)) {
      counter.closing = true;
    } else if (counter.closing && eyes <= rest + BLINK_RISE / 2) {
      counter.closing = false;
      counter.blinks += 1;
    }
  }
  readings.push({ at: now, eyes });
  return counter.blinks;
}
